<div class="course-videos-page">
  <div class="page-header">
    <div>
      <h1>Kursvideos und Fragen</h1>
      <p>Eine Excel-Datei für Videos, Lektionen, Fragen und Antworten.</p>
    </div>
    <div class="flex flex-wrap gap-2">
      <label class="primary-button cursor-pointer">
        {{ importing() ? 'Gesamtimport läuft...' : 'Gemeinsame Excel-Datei importieren' }}
        <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" class="hidden" [disabled]="importing()" (change)="importContent($event)">
      </label>
      <a class="ghost-button as-link" href="/templates/DriveMe_Academy_Gesamtimport_Template.xlsx" download>Vorlage herunterladen</a>
      <a class="ghost-button as-link" routerLink="/dashboard">Zurück zum Dashboard</a>
    </div>
  </div>

  <p class="info-message mb-5">Der Gesamtimport übernimmt Videos und Fragen gemeinsam. Bei einem Fehler wird die Datei vollständig zurückgerollt. Die Vorlage kann für weitere Module und Inhalte kopiert und angepasst werden. Eine leere Video-URL deaktiviert eine vorhandene Sprachzuordnung.</p>

  @if (error()) {
    <p class="field-error mb-4">{{ error() }}</p>
  }
  @if (notice()) {
    <p class="info-message mb-4">{{ notice() }}</p>
  }
  @if (loading()) {
    <div class="empty">Importierte Kursdaten werden geladen.</div>
  } @else if (courses().length) {
    <section class="course-videos-layout grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
      <aside class="panel section-stack content-start">
        <div class="panel-heading">
          <div>
            <h2>Module</h2>
            <p>Wähle ein Modul für die Detailübersicht.</p>
          </div>
          <button type="button" class="ghost-button" (click)="loadCatalog()">Aktualisieren</button>
        </div>

        <div class="grid gap-2">
          @for (course of courses(); track course.id) {
            <button type="button" class="w-full rounded-lg border px-3 py-3 text-left transition"
              [class.border-app-brand]="course.id === selectedCourseId()"
              [class.bg-app-brand-soft]="course.id === selectedCourseId()"
              [class.border-app-border]="course.id !== selectedCourseId()"
              [class.bg-app-surface]="course.id !== selectedCourseId()"
              (click)="selectCourse(course)">
              <div class="flex items-start justify-between gap-3">
                <strong class="text-base font-bold text-app-ink">Modul {{ course.moduleNumber }}</strong>
                <span class="badge">{{ videoCount(course) }} Videos</span>
              </div>
              <div class="mt-1 text-sm font-semibold text-app-ink">{{ course.title }}</div>
              <div class="mt-1 text-xs text-app-muted">{{ course.lessons.length }} Lektionen · {{ lessonCountWithVideos(course) }} mit Videos</div>
            </button>
          }
        </div>
      </aside>

      @if (selectedCourse(); as course) {
        <section class="panel section-stack content-start">
          <div class="panel-heading">
            <div>
              <h2>Modul {{ course.moduleNumber }} · {{ course.title }}</h2>
              <p>{{ course.lessons.length }} Lektionen · {{ videoCount(course) }} aktive Sprachvideos</p>
            </div>
            <span class="badge">Quelle: Excel</span>
          </div>

          @if (course.lessons.length) {
            <div class="overflow-x-auto">
              <table class="table">
                <thead>
                  <tr>
                    <th>Lektion</th>
                    <th>Vorhandene Sprachen</th>
                    <th>Bunny-Libraries</th>
                    <th>Videos</th>
                  </tr>
                </thead>
                <tbody>
                  @for (lesson of course.lessons; track lesson.id) {
                    <tr>
                      <td>
                        <strong>{{ lesson.lessonOrder }}. {{ lesson.title }}</strong>
                        @if (lesson.description) {
                          <div class="lesson-rich-text mt-1 text-xs text-app-muted" [innerHTML]="lesson.description"></div>
                        }
                      </td>
                      <td>
                        @if (lesson.videos.some((video) => video.active)) {
                          <div class="flex flex-wrap gap-1.5">
                            @for (video of lesson.videos; track video.id ?? video.bunnyVideoId) {
                              @if (video.active) {
                                <span class="badge">{{ languageLabel(video.language) }}</span>
                              }
                            }
                          </div>
                        } @else {
                          <span class="text-app-muted">Keine Zuordnung</span>
                        }
                      </td>
                      <td><code>{{ librarySummary(lesson) }}</code></td>
                      <td>
                        <div class="grid gap-1 text-xs">
                          @for (video of lesson.videos; track video.id ?? video.bunnyVideoId) {
                            @if (video.active) {
                              <div class="whitespace-nowrap">
                                <strong>{{ languageLabel(video.language) }}</strong>
                                <code class="ml-1">{{ video.bunnyVideoId }}</code>
                              </div>
                            }
                          }
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="empty">Für dieses Modul sind noch keine Lektionen importiert.</div>
          }

          <section class="course-question-section">
            <div class="panel-heading">
              <div>
                <h3>Fragen und Antworten</h3>
                <p>{{ questionCount(course) }} aktive Fragen für dieses Modul.</p>
              </div>
              <span class="badge">Vorschau</span>
            </div>

            @if (course.finalQuestions.length) {
              <details class="course-question-group" open>
                <summary>
                  <span>
                    <strong>Abschlussprüfung</strong>
                    <small>{{ course.finalQuestions.length }} Fragen</small>
                  </span>
                </summary>
                <div class="course-question-list">
                  @for (question of course.finalQuestions; track question.id) {
                    <article class="course-question-card">
                      <div class="course-question-heading">
                        <span class="badge">Frage {{ question.questionOrder }}</span>
                        <strong>{{ question.text }}</strong>
                      </div>
                      <ul class="course-answer-list">
                        @for (option of question.options; track option.id) {
                          <li [class.course-answer-correct]="option.correct">
                            <span>{{ option.correct ? '✓' : '○' }}</span>
                            {{ option.text }}
                            @if (option.correct) {
                              <small>Richtige Antwort</small>
                            }
                          </li>
                        }
                      </ul>
                      @if (question.explanation) {
                        <p class="course-question-explanation"><strong>Erklärung:</strong> {{ question.explanation }}</p>
                      }
                    </article>
                  }
                </div>
              </details>
            }

            @for (lesson of course.lessons; track lesson.id) {
              @if (lesson.questions.length) {
                <details class="course-question-group">
                  <summary>
                    <span>
                      <strong>{{ lesson.lessonOrder }}. {{ lesson.title }}</strong>
                      <small>{{ lesson.questions.length }} Fragen</small>
                    </span>
                  </summary>
                  <div class="course-question-list">
                    @for (question of lesson.questions; track question.id) {
                      <article class="course-question-card">
                        <div class="course-question-heading">
                          <span class="badge">Frage {{ question.questionOrder }}</span>
                          <strong>{{ question.text }}</strong>
                        </div>
                        <ul class="course-answer-list">
                          @for (option of question.options; track option.id) {
                            <li [class.course-answer-correct]="option.correct">
                              <span>{{ option.correct ? '✓' : '○' }}</span>
                              {{ option.text }}
                              @if (option.correct) {
                                <small>Richtige Antwort</small>
                              }
                            </li>
                          }
                        </ul>
                        @if (question.explanation) {
                          <p class="course-question-explanation"><strong>Erklärung:</strong> {{ question.explanation }}</p>
                        }
                      </article>
                    }
                  </div>
                </details>
              }
            }

            @if (!questionCount(course)) {
              <div class="empty">Für dieses Modul sind noch keine Fragen hinterlegt.</div>
            }
          </section>
        </section>
      }
    </section>
  } @else if (!loading()) {
    <div class="empty">Es sind noch keine importierten Kurse vorhanden.</div>
  }
</div>
